import cn from 'classnames';
import classNames from 'classnames';
import { compact, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { ScheduleParting, targetingConstants } from 'sonata-platform-components';
import { showNotification, TitleBar, useDidUpdate, usePrevious } from 'sonata-react-components';
import { isCTVCampaign, isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { CampaignFamilyEnum, MediaTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { GoalTimeFrameEnum, GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';

import { AdvertisingPlatform } from '@/components/common/AdvertisingPlatform/AdvertisingPlatform';
import { AppAndSiteMarketplace } from '@/components/common/AppAndSiteMarketplace/AppAndSiteMarketplace';
import { CampaignMarketplace } from '@/components/common/CampaignMarketplace/CampaignMarketplace';
import { ContextualBidding } from '@/components/common/ContextualBidding/ContextualBidding';
import { ContextualMacros } from '@/components/common/ContextualMacros/ContextualMacros';
import ConversionTracking from '@/components/common/ConversionTracking/ConversionTracking';
import { DynamicCreativeOptimization } from '@/components/common/DynamicCreativeOptimization';
import { MarketplaceDistribution } from '@/components/common/MarketplaceDistribution/MarketplaceDistribution';
import { MultiFlightMap } from '@/components/common/MultiFlightMap';
import { PricingModel } from '@/components/common/PricingModel';
import { Retargeting } from '@/components/common/Retargeting/Retargeting';
import { ScheduleAndDistribution } from '@/components/common/ScheduleAndDistributionSection/ScheduleAndDistribution';
import { SettingsSection } from '@/components/common/settingsSection/SettingsSection';
import { TargetingSection } from '@/components/common/TargetingSection/TargetingSection';
import { VolumetryAndForecastLI } from '@/components/common/VolumetryAndForecast/VolumetryAndForecast';
import { AdCreatives } from '@/components/ioSettings/InsertionOrderForm/Creatives/AdCreatives/AdCreatives';
import { useHighlightSection } from '@/components/ioSettings/InsertionOrderForm/hooks/useHighlightSection';
import { useItemRefs } from '@/components/ioSettings/InsertionOrderForm/hooks/useItemRefs';
import { BudgetAndGoals } from '@/components/liSettings/BudgetAndGoals/BudgetAndGoals.tsx';
import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import {
  AppAndSiteChannel,
  AppAndSitesCategories,
  AudienceTargeting,
  ContentCategories,
  Demography,
  DeviceTargeting,
  LQIQualityAssurance,
  MediaQualityAssuranceSystem,
  MobileDeviceAndConnectivity,
  SupplySource,
  TemperatureAndWeather
} from '@/components/Targeting';
import { hasContentCategoriesPermission } from '@/components/Targeting/ContentCategories/helpers/hasContentCategoriesPermission.helper';
import { hasMediaQualityPermission } from '@/components/Targeting/MediaQualityAssuranceSystem/helpers/hasMediaQualityPermission.helper';
import TargetingProvider from '@/contexts/Targeting/TargetingProvider';
import { getCountriesValuesOptions } from '@/helpers/countriesOptions/countriesOptions.helper.ts';
import { getUserBidModelsAndMargins } from '@/helpers/user/user.helpers';
import { useIsEntityInState } from '@/hooks/useIsEntityInState';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getSession } from '@/store/global/session/selectors';
import { getUser, reloadUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { useStore as useLineItemStore } from '@/store/local/lineItem';
import { useInsertionOrderSchedules } from './hooks/useInsertionOrderSchedules';
import { LineItemFormContainerStyled, LineItemFormStyled } from './LineItemForm.styled';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { LineItem } from 'sonata-static-storage/lib/entities';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { MediaTypeType } from 'sonata-static-storage/lib/schemas/mediaType/MediaType.types';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { LineItemStore } from '@/store/local/lineItem';

type LineItemFormRef = {
  scrollToSection: (sectionId: string) => void;
};

type Props = {
  className: string;
  isEditMode: boolean;
};

function LineItemForm({ className, isEditMode }: Props, ref: React.Ref<LineItemFormRef>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();
  const session = useGlobalStore(getSession);
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const location = useLocation();
  const sessionId = session && !isEmpty(session) ? session?.toJSON().sessionId : '';
  const state = useLineItemStore() as LineItemStore;
  const {
    activeSubsection,
    beginDate,
    endDate,
    errors,
    isSnliActive,
    list,
    name,
    screens,
    selectedSection,
    setActiveSubsection,
    setAds: setFormAds,
    setAdsDistribution: setFormAdsDistribution,
    setAdsPriceEnabled: setFormAdsPriceEnabled,
    setBeginDate: setFormBeginDate,
    setCountries: setFormCountries,
    setDco: setFormDco,
    setEndDate: setFormEndDate,
    setList,
    setName: setFormLiName,
    setPricingModelConfig: setFormPricingModelConfig,
    setScheduleParting: setFormScheduleParting,
    setScreens,
    setTargeting: setFormTargeting,
    setUserFrequencyCapping: setFormUserFrequencyCapping,
    setZones,
    zones
  } = useLineItemFormContext();
  const { getRef } = useItemRefs();
  const { scrollToSection } = useHighlightSection(getRef, setActiveSubsection, containerRef, activeSubsection);
  useImperativeHandle(ref, () => ({ scrollToSection }));

  const {
    _id,
    ads,
    attributionSettings,
    contextualBidding,
    countries,
    dco,
    doohAudience,
    goals,
    entity,
    isLoading: isLineItemSettingsLoading,
    mediaType,
    pendingToCreate,
    pixels,
    pricingModelConfig,
    resetMapSave,
    saveLineItem,
    scheduleParting,
    screenGroups,
    setAds,
    setAdsDistribution,
    setAdsPriceEnabled,
    setAttributionSettings,
    setBeginDate,
    setContextualBidding,
    setCountries,
    setCustomEvents,
    setDco,
    setDoohAudience,
    setEndDate,
    setLiName,
    setHasUnsavedMapChanges,
    setMediaType,
    setPendingSaveSteps,
    setPixels,
    setPricingModelConfig,
    setSaveStep,
    setScheduleParting,
    setScreenGroups,
    setTargeting,
    setTimezone,
    setUserFrequencyCapping,
    setVenues,
    setZoneGroups,
    shouldMapSave,
    targeting,
    type: mediaChannelSelected,
    userFrequencyCapping,
    venues,
    zoneGroups
  } = state as LineItemStore;

  const userJSON = user.toJSON();
  // the IO of an existing line item is not in the store (only in the route state when creating)
  const ioSchedules = useInsertionOrderSchedules(
    String(user.get('id')),
    (entity.toJSON().insertionOrder ?? location.state?.insertionOrder?._id) as string | undefined
  );

  const bidPriceConfig = pricingModelConfig.bidModels[0].bid.pricingModel;
  const previousBidPriceConfig = usePrevious(bidPriceConfig);

  useDidUpdate(() => {
    if (
      previousBidPriceConfig !== undefined &&
      previousBidPriceConfig !== bidPriceConfig &&
      ads.some(ad => !ad.deleted && ad.visible && ad.bidPrice > 0)
    ) {
      setAds(ads.map(ad => (!ad.deleted && ad.visible && ad.bidPrice > 0 ? { ...ad, bidPrice: 0 } : ad)));
      setFormAds(ads.map(ad => (!ad.deleted && ad.visible && ad.bidPrice > 0 ? { ...ad, bidPrice: 0 } : ad)));
      showNotification(
        'map',
        formatMessage({
          defaultMessage: 'The pricing model has been updated. All ad prices have been reset to their default values.',
          id: 'ioSettings.pricingModelChanged'
        })
      );
    }
  }, [bidPriceConfig, previousBidPriceConfig]);

  // ppsv (CPSV) only allows day-of-week scheduling: switching to ppsv resets a year-date schedule to the full week.
  useDidUpdate(() => {
    if (bidPriceConfig === PricingModelEnum.ppsv && scheduleParting.yearDate.enabled) {
      const weekConfig = Object.fromEntries(
        ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => [
          day,
          Array.from({ length: 24 }, (_, hour) => hour)
        ])
      );
      const weekScheduleParting = {
        ...scheduleParting,
        config: weekConfig,
        enabled: false,
        yearDate: { aggregated: false, enabled: false }
      };
      setScheduleParting(weekScheduleParting);
      setFormScheduleParting(weekScheduleParting);
    }
  }, [bidPriceConfig]);

  useEffect(() => {
    return () => {
      taptap.entity = {
        model: undefined,
        validationErrors: undefined
      };
    };
  }, []);

  taptap.entity = {
    ...taptap.entity,
    model: entity.toJSON()
  };

  const showConversionTracking =
    checkUserResourcePermissions('campaignPixels', userJSON, userRoles) && mediaChannelSelected !== 'dooh';
  const showDeviceTargeting = checkUserResourcePermissions('deviceIdImporter', userJSON, userRoles);
  const showRetargeting = checkUserResourcePermissions('retargetingSection', userJSON, userRoles);
  const showAudienceTargeting =
    checkUserResourcePermissions('audienceThirdParty', userJSON, userRoles) ||
    checkUserResourcePermissions('audienceFirstParty', userJSON, userRoles) ||
    checkUserResourcePermissions('audienceAffinity', userJSON, userRoles) ||
    checkUserResourcePermissions('audienceCustom', userJSON, userRoles);
  const showLQIIndex = checkUserResourcePermissions('lqi', userJSON, userRoles);
  const showContentCategories = hasContentCategoriesPermission(userJSON, userRoles);
  const showMediaQualityAssurance = hasMediaQualityPermission(userJSON, userRoles);
  const showContextualMacros = mediaChannelSelected !== 'dooh';
  const showSupplySourceSection = checkUserResourcePermissions('supplySource', userJSON, userRoles);

  const getDefaultCountries = (): SelectorOptionPropsInterface[] => {
    if (isEditMode) {
      return getCountriesValuesOptions(countries);
    }
    return getCountriesValuesOptions(location.state?.insertionOrder?.countries);
  };

  const defaultCountries = getDefaultCountries();

  const handlePricingModelChange = (newPricingModelConfig: PricingModelConfig) => {
    setPricingModelConfig(newPricingModelConfig);
    setFormPricingModelConfig(newPricingModelConfig);
  };

  const handleCountriesChange = (options: SelectorOptionPropsInterface[]) => {
    const countryId = options.map(option => option.id as string);
    setFormCountries(countryId);
    setCountries(countryId);
  };

  const handleTimezonesChange = (options: SelectorOptionPropsInterface) => {
    setTimezone(options.id as string);
  };

  const handleLiNameChange = (value: string | FileList | null) => {
    if (typeof value === 'string') {
      setFormLiName(value);
      setLiName(value);
    }
  };

  const handleDcoChange = (newDco: LineItem['dco']) => {
    setFormDco(newDco);
    setDco(newDco);
  };

  const handleAdsChange = (ads: LineItem['ads']) => {
    setAds(ads);
    setFormAds(ads);
  };

  const handleAdsDistributionToggled = (newState: boolean) => {
    setAdsDistribution(newState);
    setFormAdsDistribution(newState);
  };

  const handleAdsPriceToggled = (newState: boolean) => {
    setAdsPriceEnabled(newState);
    setFormAdsPriceEnabled(newState);
  };

  const handleCustomEventsToggled = (newState: boolean) => {
    setCustomEvents(newState);
  };

  useEffect(() => {
    handleCountriesChange(defaultCountries);
  }, []);

  const handleSetTargeting = (newTargeting: TargetingBaseType) => {
    const cleanCarriers: Record<string, string>[] = [];
    if (newTargeting.carriers && !isEmpty(newTargeting.carriers)) {
      const carriers = compact(
        newTargeting.carriers.filter((carrierEntry: Record<string, any>) => !isEmpty(carrierEntry))
      ).flat();
      cleanCarriers.push(...(carriers as Record<string, any>[]));
    }
    const newCleanedTargeting: TargetingBaseType = {
      ...newTargeting,
      carriers: cleanCarriers
    };

    setFormTargeting(newCleanedTargeting);
    setTargeting(newCleanedTargeting);
  };

  const handleMapSaveComplete = () => {
    resetMapSave();
  };

  const isStandard =
    mediaChannelSelected && isDigitalCampaign(mediaChannelSelected) && !isCTVCampaign(mediaChannelSelected);
  const isDooh = mediaChannelSelected && isDOOHCampaign(mediaChannelSelected);
  const isFinished = useIsEntityInState('lineItem', pendingToCreate ? undefined : _id, ['finished']);
  const isCTV = mediaChannelSelected && isCTVCampaign(mediaChannelSelected);
  const showMainSettingsSection = selectedSection === 'mainSettings';
  // The locations/screens section is the same physical block in the UI;
  // the active section name differs by campaign family (`'screens'` for
  // DOOH, `'locations'` otherwise). Treat both as "show the
  // locations-or-screens section".
  const showLocationsSection = selectedSection === 'locations' || selectedSection === 'screens';
  const showContextTargetingSection = selectedSection === 'contextTargeting';
  const showCreativesSection = selectedSection === 'creatives';

  const handleDevicePlatformChange = (devicePlatform: TargetingBaseType['devicePlatform']) => {
    const newTargeting = {
      ...targeting,
      devicePlatform
    } as TargetingBaseType;
    setTargeting(newTargeting);
  };

  const handleCampaignMarketplaceChange = (newMediaType: MediaTypeType, prevMediaType: MediaTypeType) => {
    setMediaType(newMediaType);
    const newMarginsAndBidModels = getUserBidModelsAndMargins(
      prevMediaType,
      newMediaType,
      pricingModelConfig.bidModels,
      userJSON
    );

    const newPricingModelConfig = structuredClone(pricingModelConfig);

    setPricingModelConfig({
      ...newPricingModelConfig,
      bidModels: newMarginsAndBidModels.bidModels,
      margin: newMarginsAndBidModels.margins
    });

    if (newMediaType === 'openRtb' && prevMediaType !== 'openRtb') {
      const newTargeting = {
        ...targeting,
        deals: targetingConstants.DEFAULT_DEALS_CONFIGURATION.deals
      } as TargetingBaseType;
      setTargeting(newTargeting);
      setFormTargeting(newTargeting);

      if (targeting?.deals?.length) {
        showNotification(
          'info',
          formatMessage({
            defaultMessage: 'Marketplace type changed to "Open".',
            id: 'ioSettings.marketplaceDealsReset.title'
          }),
          {
            description: formatMessage({
              defaultMessage:
                'Previously selected deals have been reset, as they don’t apply to this marketplace type.',
              id: 'ioSettings.marketplaceDealsReset.description'
            }),
            icon: 'fa-regular fa-circle-exclamation'
          }
        );
      }
    }
  };

  const handleMarketplaceDistributionChange = (premiumDistribution: number, openDistribution: number) => {
    const newPricingModelConfig = structuredClone(pricingModelConfig);
    newPricingModelConfig.bidModels = newPricingModelConfig.bidModels.map(bidModel => {
      if (bidModel.mediaType === 'pmp') {
        return { ...bidModel, distribution: premiumDistribution };
      }
      if (bidModel.mediaType === 'openRtb') {
        return { ...bidModel, distribution: openDistribution };
      }
      return bidModel;
    });
    setPricingModelConfig(newPricingModelConfig);
  };

  const handleLineItemsDatesChange = (dates: { beginDate: Date | null; endDate: Date | null }) => {
    setBeginDate(dates.beginDate);
    setFormBeginDate(dates.beginDate);
    setEndDate(dates.endDate);
    setFormEndDate(dates.endDate);

    if (scheduleParting.yearDate.enabled && !scheduleParting.yearDate.aggregated && dates.beginDate && dates.endDate) {
      const beginDateKey = moment.utc(dates.beginDate).format('YYYY-MM-DD');
      const endDateKey = moment.utc(dates.endDate).format('YYYY-MM-DD');
      const prunedScheduleParting = {
        ...scheduleParting,
        config: Object.fromEntries(
          Object.entries(scheduleParting.config).filter(([dateKey]) => dateKey >= beginDateKey && dateKey <= endDateKey)
        )
      };
      setScheduleParting(prunedScheduleParting);
      setFormScheduleParting(prunedScheduleParting);
    }
  };

  const handleSchedulePartingChange = (schedulePartingData: ScheduleParting) => {
    setFormScheduleParting(schedulePartingData);
    setScheduleParting(schedulePartingData);
  };

  const handleUserFrequencyCappingChange = (userFrequencyCappingData: FrequencyCapping) => {
    setFormUserFrequencyCapping(userFrequencyCappingData);
    setUserFrequencyCapping(userFrequencyCappingData);
  };

  const reloadUserToken = {};
  const handleReloadUser = async () => {
    await reloadUser(reloadUserToken);
  };

  return (
    <LineItemFormContainerStyled>
      {isDooh && !isSnliActive && !showLocationsSection ? <VolumetryAndForecastLI /> : null}
      <LineItemFormStyled
        className={`liSettings ${className}`}
        isDooh={isDooh}
        ref={containerRef}
        showLocations={showLocationsSection}
      >
        {mediaChannelSelected && (
          <>
            {showMainSettingsSection && (
              <div ref={getRef('mainSettings')}>
                <TitleBar
                  className={classNames('liSettings__titleBarSection', {
                    'liSettings__titleBarSection--dooh': isDooh
                  })}
                  id="mediaChannelSelectionSection-title"
                  title={formatMessage({
                    defaultMessage: 'Main Settings',
                    id: 'ioSettings.mainSettingsTitle'
                  })}
                />
                <div className="liSettings__mainSettingsContainer">
                  <div
                    className={cn('liSettings__subsection', {
                      'liSettings__subsection--selected': activeSubsection === 'ioSettings'
                    })}
                    id="ioSettings"
                    ref={getRef('ioSettings')}
                  >
                    <SettingsSection
                      countries={countries}
                      defaultCountries={defaultCountries}
                      entity="lineItem"
                      errors={errors}
                      name={name}
                      onCountriesChange={handleCountriesChange}
                      onIoNameChange={handleLiNameChange}
                      onTimeZoneChange={handleTimezonesChange}
                      timezone={entity.toJSON().timezone}
                    />
                  </div>
                  {isStandard ? (
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'advertisingPlatform'
                      })}
                      id="advertisingPlatform"
                      ref={getRef('advertisingPlatform')}
                    >
                      <AdvertisingPlatform
                        budget={
                          goals.find(g => g.timeFrame === 'overall' && g.type === 'budget' && !!g.target)?.target || 100
                        }
                        devicePlatform={targeting!.devicePlatform}
                        onDevicePlatformChange={handleDevicePlatformChange}
                        unknownIncluded={targeting?.unknownIncluded.deviceType || false}
                      />
                    </div>
                  ) : null}
                </div>
                <div
                  className={cn('liSettings__subsection', 'liSettings__scheduleAndDistribution', {
                    'liSettings__subsection--selected': activeSubsection === 'schedulingDistribution'
                  })}
                  id="schedulingDistribution"
                  ref={getRef('schedulingDistribution')}
                >
                  <ScheduleAndDistribution
                    beginDate={entity.toJSON().beginDate}
                    completed={entity.toJSON().completed}
                    contextualBidding={entity.toJSON().contextualBidding}
                    endDate={entity.toJSON().endDate}
                    entityType="lineItem"
                    errors={errors}
                    id={entity.toJSON()._id}
                    ioFlights={ioSchedules.flights}
                    ioLineItems={ioSchedules.lineItems}
                    isDooh={isDooh}
                    lineItemName={name}
                    onChangeLineItemDates={handleLineItemsDatesChange}
                    onChangeTimezone={setTimezone}
                    onSchedulePartingChange={handleSchedulePartingChange}
                    onUserFrequencyCappingChange={handleUserFrequencyCappingChange}
                    pricingModelConfig={pricingModelConfig}
                    scheduleParting={scheduleParting}
                    timezone={entity.toJSON().timezone}
                    user={userJSON}
                    userFrequencyCapping={userFrequencyCapping}
                    userRoles={userRoles}
                  />
                </div>
                {!isDooh && !isCTV && userJSON.mediaTypes.length > 1 ? (
                  <div className="liSettings__campaignMarketplaceContainer">
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'campaignMarketplace'
                      })}
                      id="campaignMarketplace"
                      ref={getRef('campaignMarketplace')}
                    >
                      <CampaignMarketplace
                        marketPlace={mediaType as MediaTypeType}
                        onCampaignMarketplaceChange={handleCampaignMarketplaceChange}
                      />
                    </div>
                    {mediaType === 'hybrid' ? (
                      <MarketplaceDistribution
                        bidModels={pricingModelConfig.bidModels}
                        budget={
                          goals.find(g => g.timeFrame === 'overall' && g.type === 'budget' && !!g.target)?.target || 100
                        }
                        onChange={handleMarketplaceDistributionChange}
                      />
                    ) : null}
                  </div>
                ) : null}
                <div
                  className={cn('liSettings__subsection', 'liSettings__budgetAndGoals', {
                    'liSettings__subsection--selected': activeSubsection === 'budgetAndGoals'
                  })}
                  id="budgetAndGoals"
                  ref={getRef('budgetAndGoals')}
                >
                  <BudgetAndGoals />
                </div>
                <div
                  className={cn('liSettings__subsection', 'liSettings__pricingModel', {
                    'liSettings__subsection--selected': activeSubsection === 'pricingModel'
                  })}
                  id="pricingModel"
                  ref={getRef('pricingModel')}
                >
                  <PricingModel
                    beginDate={beginDate || undefined}
                    currency={baseCurrency}
                    endDate={endDate || undefined}
                    errors={errors?.pricingModelConfig}
                    id={'pricingModel-id'}
                    mediaType={MediaTypeEnum[mediaType as keyof typeof MediaTypeEnum]}
                    onChange={handlePricingModelChange}
                    pricingModelConfig={pricingModelConfig}
                    type={mediaChannelSelected}
                    user={userJSON}
                    userRoles={userRoles}
                  />
                </div>
              </div>
            )}
            <div className="liSettings__map" ref={getRef('locations')}>
              <MultiFlightMap
                activeSubsection={activeSubsection}
                ads={ads}
                attributionSettings={attributionSettings}
                countries={countries}
                currentSection={selectedSection}
                entityId={entity.toJSON()._id!}
                entityIsDraft={!entity.toJSON().completed}
                entityType="lineItem"
                id={'multiFlightMap'}
                isLoading={isLineItemSettingsLoading}
                key={mediaChannelSelected}
                list={list}
                mapStyle="streets"
                onChangeAds={handleAdsChange}
                onChangeAttributionSettings={setAttributionSettings}
                onChangeItems={{
                  screens: setScreens,
                  zones: setZones
                }}
                onChangeScreenGroups={setScreenGroups}
                onChangeZoneGroups={setZoneGroups}
                onPendingSaveStepsChange={setPendingSaveSteps}
                onRefreshUser={handleReloadUser}
                onSaveComplete={handleMapSaveComplete}
                onSaveStepChange={setSaveStep}
                onUnsavedMapChangesChange={setHasUnsavedMapChanges}
                onVenuesChange={setVenues}
                saveSettings={saveLineItem}
                screenGroups={screenGroups}
                setList={setList}
                shouldMapSave={shouldMapSave}
                targeting={targeting as TargetingBaseType}
                type={mediaChannelSelected}
                user={user.toJSON()}
                userRoles={userRoles}
                venues={venues}
                volumetryAndForecast={isDooh && !isSnliActive ? <VolumetryAndForecastLI /> : undefined}
                zoneGroups={zoneGroups}
                zones={zones}
              />
            </div>
            {showContextTargetingSection && (
              <div ref={getRef('contextTargeting')}>
                <div className="liSettings__targeting">
                  <div className="liSettings__targetingContainer">
                    <TitleBar
                      className={classNames('liSettings__titleBar', {
                        'liSettings__titleBar--dooh': isDooh
                      })}
                      id={'liSettings-targetingTitle'}
                      title={formatMessage({
                        defaultMessage: 'Context & Targeting',
                        id: 'ioSettings.targetingTitle'
                      })}
                    />
                    <TargetingSection
                      description={formatMessage({
                        defaultMessage:
                          'Define exactly who will see your ads based on user profiles, past interactions, and demographic data.',
                        id: 'ioSettings.targetingSection.description.audienceAndUsers'
                      })}
                      label={formatMessage({
                        defaultMessage: 'Audience & Users',
                        id: 'ioSettings.targetingSection.audienceAndUsers'
                      })}
                    />
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'demography'
                      })}
                      id="demography"
                      ref={getRef('demography')}
                    >
                      <Demography
                        country={countries?.[0]}
                        doohAudience={doohAudience}
                        familyType={mediaChannelSelected}
                        isReadOnly={isFinished}
                        onChange={handleSetTargeting}
                        onDoohAudienceChange={setDoohAudience}
                        targeting={targeting}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      />
                    </div>
                    {!isDooh && (
                      <>
                        {showRetargeting && (
                          <div id={'retargeting'} ref={getRef('retargeting')}>
                            <TargetingProvider
                              familyType={mediaChannelSelected}
                              onChange={handleSetTargeting}
                              showDeals={isDooh}
                              targeting={targeting as TargetingBaseType}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            >
                              <Retargeting
                                className={cn('liSettings__subsection', {
                                  'liSettings__subsection--selected': activeSubsection === 'retargeting'
                                })}
                                onSetZones={setZones}
                                zones={zones}
                              />
                            </TargetingProvider>
                          </div>
                        )}
                        {showAudienceTargeting && (
                          <div
                            className={cn('liSettings__subsection', {
                              'liSettings__subsection--selected': activeSubsection === 'audienceTargeting'
                            })}
                            id={'audienceTargeting'}
                            ref={getRef('audienceTargeting')}
                          >
                            <AudienceTargeting
                              baseCurrency={baseCurrency}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {!isDooh && showDeviceTargeting && (
                      <div
                        className={cn('liSettings__subsection', {
                          'liSettings__subsection--selected': activeSubsection === 'deviceTargeting'
                        })}
                        id="deviceTargeting"
                        ref={getRef('deviceTargeting')}
                      >
                        <DeviceTargeting
                          campaignId={entity.toJSON()._id}
                          onChange={handleSetTargeting}
                          sessionId={sessionId}
                          targeting={targeting}
                          user={user.toJSON()}
                          userRoles={userRoles}
                        />
                      </div>
                    )}
                    <TargetingSection
                      description={formatMessage({
                        defaultMessage:
                          'Select the technical specifications for your campaign, including device types, connections, and specific inventory sources.',
                        id: 'ioSettings.targetingSection.description.technologyAndSupply'
                      })}
                      label={formatMessage({
                        defaultMessage: 'Technology & Supply',
                        id: 'ioSettings.targetingSection.technologyAndSupply'
                      })}
                    />
                    {!isDooh && (
                      <>
                        <div
                          className={cn('liSettings__subsection', {
                            'liSettings__subsection--selected': activeSubsection === 'mobileDevicesConnectivity'
                          })}
                          id="mobileDevicesConnectivity"
                          ref={getRef('mobileDevicesConnectivity')}
                        >
                          <MobileDeviceAndConnectivity
                            countries={countries}
                            familyType={mediaChannelSelected}
                            onChange={handleSetTargeting}
                            targeting={targeting}
                            user={user.toJSON()}
                            userRoles={userRoles}
                          />
                        </div>
                        {showSupplySourceSection && (
                          <div
                            className={cn('liSettings__subsection', {
                              'liSettings__subsection--selected': activeSubsection === 'supplySource'
                            })}
                            id="supplySource"
                            ref={getRef('supplySource')}
                          >
                            <SupplySource
                              baseCurrency={baseCurrency}
                              error={errors?.targeting?.adExchanges}
                              flightsGoals={entity.toJSON().goals.map(goal => {
                                return {
                                  target: goal.target || 0,
                                  timeFrame: goal.timeFrame as GoalTimeFrameEnum,
                                  type: goal.type as GoalTypeEnum
                                };
                              })}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'appMarketplace'
                      })}
                      id="appMarketplace"
                      ref={getRef('appMarketplace')}
                    >
                      <TargetingProvider
                        familyType={mediaChannelSelected}
                        onChange={handleSetTargeting}
                        showDeals={mediaType === 'pmp' || mediaType === 'hybrid'}
                        targeting={targeting as TargetingBaseType}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      >
                        <AppAndSiteMarketplace
                          entityId={entity.toJSON()._id!}
                          error={errors?.targeting?.appAndSiteMarketplace}
                          goals={goals}
                          id={`appMarketplace${entity.toJSON()._id!}`}
                          pricingModelConfig={pricingModelConfig}
                          sessionId={sessionId}
                        />
                      </TargetingProvider>
                    </div>
                    <TargetingSection
                      description={formatMessage({
                        defaultMessage:
                          'Control where and when your ads appear by targeting specific content themes, channels, or real-world conditions.',
                        id: 'ioSettings.targetingSection.description.contextAndEnvironment'
                      })}
                      label={formatMessage({
                        defaultMessage: 'Context & Environment',
                        id: 'ioSettings.targetingSection.contextAndEnvironment'
                      })}
                    />
                    {!isDooh && (
                      <>
                        <div
                          className={cn('liSettings__subsection', {
                            'liSettings__subsection--selected': activeSubsection === 'appSiteCategories'
                          })}
                          id="appSiteCategories"
                          ref={getRef('appSiteCategories')}
                        >
                          <AppAndSitesCategories
                            onChange={handleSetTargeting}
                            targeting={targeting}
                            user={user.toJSON()}
                            userRoles={userRoles}
                          />
                        </div>
                        <div
                          className={cn('liSettings__subsection', {
                            'liSettings__subsection--selected': activeSubsection === 'appSiteChannel'
                          })}
                          id="appSiteChannel"
                          ref={getRef('appSiteChannel')}
                        >
                          <AppAndSiteChannel
                            onChange={handleSetTargeting}
                            targeting={targeting}
                            user={user.toJSON()}
                            userRoles={userRoles}
                          />
                        </div>
                        {showContentCategories && (
                          <div
                            className={cn('liSettings__subsection', {
                              'liSettings__subsection--selected': activeSubsection === 'contentCategories'
                            })}
                            id="contentCategories"
                            ref={getRef('contentCategories')}
                          >
                            <ContentCategories
                              baseCurrency={baseCurrency}
                              error={errors?.targeting?.contextualTargeting}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              totalBudget={
                                entity
                                  .toJSON()
                                  .goals.find(g => g.timeFrame === 'overall' && g.type === 'budget' && !!g.target)
                                  ?.target || 0
                              }
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'temperatureAndWeather'
                      })}
                      id="temperatureAndWeather"
                      ref={getRef('temperatureAndWeather')}
                    >
                      <TemperatureAndWeather
                        error={errors?.targeting?.temperatureAndWeather?.temperature}
                        onChange={handleSetTargeting}
                        targeting={targeting}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      />
                    </div>
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'contextualBidding'
                      })}
                      id="contextualBidding"
                      ref={getRef('contextualBidding')}
                    >
                      <ContextualBidding
                        campaign={entity.toJSON()}
                        contextualBidding={contextualBidding}
                        currencyInfo={baseCurrency}
                        entityType="lineItem"
                        goal={
                          entity
                            .toJSON()
                            .goals.find(g => g.timeFrame === 'overall' && g.type === 'budget' && !!g.target)?.target ||
                          0
                        }
                        groupsConfig={{
                          groups: mediaChannelSelected === CampaignFamilyEnum.dooh ? screenGroups : zoneGroups,
                          items:
                            mediaChannelSelected === CampaignFamilyEnum.dooh
                              ? (screens as GeoDataAndScreensInteface[])
                              : (zones as Zone[])
                        }}
                        id="multiFlightContextualBidding"
                        onChange={setContextualBidding}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      />
                    </div>
                    {!isDooh && (
                      <>
                        {(showLQIIndex || showMediaQualityAssurance) && (
                          <TargetingSection
                            description={formatMessage({
                              defaultMessage:
                                'Protect your brand and ensure high performance by applying traffic quality filters and brand safety assurance systems.',
                              id: 'ioSettings.targetingSection.description.qualityAndBrandSafety'
                            })}
                            label={formatMessage({
                              defaultMessage: 'Quality & Brand Safety',
                              id: 'ioSettings.targetingSection.qualityAndBrandSafety'
                            })}
                          />
                        )}
                        {showLQIIndex && (
                          <div
                            className={cn('liSettings__subsection', {
                              'liSettings__subsection--selected': activeSubsection === 'locationQualityIndexes'
                            })}
                            id="locationQualityIndexes"
                            ref={getRef('locationQualityIndexes')}
                          >
                            <LQIQualityAssurance
                              familyType={mediaChannelSelected}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                        {showMediaQualityAssurance && (
                          <div
                            className={cn('liSettings__subsection', {
                              'liSettings__subsection--selected': activeSubsection === 'mediaQualityAssurance'
                            })}
                            id="mediaQualityAssurance"
                            ref={getRef('mediaQualityAssurance')}
                          >
                            <MediaQualityAssuranceSystem
                              currency={baseCurrency}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {showCreativesSection ? (
              <div className="liSettings__creatives" ref={getRef('creatives')}>
                <TitleBar
                  className={classNames('liSettings__titleBarSection', {
                    'liSettings__titleBarSection--dooh': isDooh
                  })}
                  id="liSettings-creativesTitle"
                  title={formatMessage({
                    defaultMessage: 'Creatives',
                    id: 'ioSettings.creatives.title'
                  })}
                />
                <div className="liSettings__creativesContainer">
                  <div
                    className={cn('liSettings__subsection', {
                      'liSettings__subsection--selected': activeSubsection === 'adCreatives'
                    })}
                    id="adCreatives"
                    ref={getRef('adCreatives')}
                  >
                    <AdCreatives
                      entity={entity.toJSON()}
                      entityType="lineItem"
                      errors={errors}
                      goals={goals.filter(goal => goal.timeFrame === 'overall' && goal.type === 'budget')}
                      onAdsChange={handleAdsChange}
                      onAdsDistributionToggled={handleAdsDistributionToggled}
                      onAdsPriceToggled={handleAdsPriceToggled}
                      onCustomEventsToggled={handleCustomEventsToggled}
                      onVenuesChange={setVenues}
                      savedBaseline={state.savedBaseline}
                      screens={screens ?? []}
                      user={user.entity}
                      userRoles={userRoles}
                      venues={venues}
                    />
                  </div>
                  {showConversionTracking && (
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'conversionTracking'
                      })}
                      id="conversionTracking"
                      ref={getRef('conversionTracking')}
                    >
                      <ConversionTracking id="liConversionTracking" onPixelsChange={setPixels} pixels={pixels} />
                    </div>
                  )}
                  <div
                    className={cn('liSettingss__subsection', {
                      'liSettings__subsection--selected': activeSubsection === 'contextualSettings'
                    })}
                    id="contextualSettings"
                    ref={getRef('contextualSettings')}
                  >
                    <DynamicCreativeOptimization
                      ads={ads}
                      campaign={entity.toJSON()}
                      countries={countries}
                      dco={dco!}
                      error={
                        errors?.dco?.scenarios &&
                        (Object.values(errors.dco.scenarios as object) as any[]).find(s => s?.ads)?.ads
                      }
                      id="dynamicCreativeOptimization"
                      onDcoChange={handleDcoChange}
                      user={user.toJSON()}
                      userRoles={userRoles}
                    />
                  </div>
                  {showContextualMacros && (
                    <div
                      className={cn('liSettings__subsection', {
                        'liSettings__subsection--selected': activeSubsection === 'contextualMacros'
                      })}
                      id="contextualMacros"
                      ref={getRef('contextualMacros')}
                    >
                      <ContextualMacros
                        ads={ads}
                        countries={countries}
                        entityId={entity.toJSON()._id}
                        entityIsActive={entity.toJSON().completed}
                        id="liContextualMacros"
                        onAdsChange={setAds}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </LineItemFormStyled>
    </LineItemFormContainerStyled>
  );
}

LineItemForm.displayName = 'LineItemForm';

export default React.forwardRef<LineItemFormRef, Props>(LineItemForm);
